import React, { useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import TuneIcon from "@mui/icons-material/Tune";
import { DATE_FIELDS, DATE_PRESETS, completeAdvanced } from "./filterState";
import { DayPicker } from "./ValueEditor";
import { FilterBuilder } from "./FilterBuilder";
import { useFieldValues, useFirstReportDate } from "./useReportFields";

const countConditions = (node) =>
  node ? node.clauses.reduce((n, c) => n + (Array.isArray(c.clauses) ? countConditions(c) : 1), 0) : 0;

/**
 * The report's filters: species and dates up front, the exclusions, and an
 * advanced builder for conditions on any registry field. Edits a draft; the
 * shell's Generate button applies it. `fields` is the registry; the builder
 * offers the ones that can be filtered on.
 */
export const ReportFilters = ({ value, onChange, fields: registry }) => {
  const fields = useMemo(() => registry.filter((f) => f.filterOps?.length), [registry]);
  const species = useFieldValues("summary_species");
  const firstDate = useFirstReportDate();
  const [presetAnchor, setPresetAnchor] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(Boolean(completeAdvanced(value.advanced)));
  const set = (patch) => onChange({ ...value, ...patch });
  const setDates = (patch) => set({ dates: { ...value.dates, ...patch } });

  const applyPreset = async (preset) => {
    setPresetAnchor(null);
    const first = preset.key === "allTime" ? await firstDate().catch(() => null) : null;
    setDates(preset.range(new Date(), first));
  };

  const advancedCount = countConditions(completeAdvanced(value.advanced));

  return (
    <Box id="report-filter-panel" sx={{ px: { xs: 2, md: 3 }, pb: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-start" }}>
        <Autocomplete
          multiple
          size="small"
          options={species.values.map((o) => o.value)}
          loading={species.loading}
          value={value.species}
          onChange={(_, v) => set({ species: v })}
          disableCloseOnSelect
          limitTags={3}
          renderInput={(params) => (
            <TextField {...params} label="Species" placeholder={value.species.length ? "" : "All species"} />
          )}
          sx={{ flex: "1 1 280px", minWidth: 260, maxWidth: 560 }}
        />
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", flexWrap: "wrap" }}>
          <TextField
            select
            size="small"
            label="Dates by"
            value={value.dates.field}
            onChange={(e) => setDates({ field: e.target.value })}
            sx={{ width: 160 }}
          >
            {DATE_FIELDS.map((f) => (
              <MenuItem key={f.key} value={f.key}>
                {f.label}
              </MenuItem>
            ))}
          </TextField>
          <DayPicker label="From" value={value.dates.from} maxDay={value.dates.to} onChange={(from) => setDates({ from })} />
          <DayPicker label="To" value={value.dates.to} minDay={value.dates.from} onChange={(to) => setDates({ to })} />
          <Button
            size="small"
            onClick={(e) => setPresetAnchor(e.currentTarget)}
            endIcon={<ArrowDropDownIcon />}
            aria-haspopup="menu"
            sx={{ mt: 0.5 }}
          >
            Quick dates
          </Button>
          <Menu anchorEl={presetAnchor} open={Boolean(presetAnchor)} onClose={() => setPresetAnchor(null)}>
            {DATE_PRESETS.map((p) => (
              <MenuItem key={p.key} onClick={() => applyPreset(p)}>
                {p.label}
              </MenuItem>
            ))}
          </Menu>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <FormControlLabel
            control={<Checkbox size="small" checked={value.excludeEmpty} onChange={(e) => set({ excludeEmpty: e.target.checked })} />}
            label={<Typography variant="body2">Exclude empty cells</Typography>}
            sx={{ m: 0, mt: -0.5 }}
          />
          <FormControlLabel
            control={<Checkbox size="small" checked={value.excludeUnknown} onChange={(e) => set({ excludeUnknown: e.target.checked })} />}
            label={<Typography variant="body2">Exclude unknown species</Typography>}
            sx={{ m: 0, mt: -1 }}
          />
        </Box>
      </Box>

      <Box>
        <Button
          size="small"
          startIcon={<TuneIcon />}
          onClick={() => setAdvancedOpen(!advancedOpen)}
          aria-expanded={advancedOpen}
          aria-controls="advanced-filters"
        >
          {advancedOpen ? "Hide advanced filters" : "Advanced filters"}
          {advancedCount > 0 && ` (${advancedCount})`}
        </Button>
        {advancedOpen && (
          <Box id="advanced-filters" sx={{ mt: 1 }}>
            <FilterBuilder fields={fields} value={value.advanced} onChange={(advanced) => set({ advanced })} />
          </Box>
        )}
      </Box>
    </Box>
  );
};
